import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type ConfirmationDialogProps = {
  title: string
  onConfirm: () => unknown | Promise<unknown>
  message?: React.ReactNode
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  open?: boolean
  onClose?: () => void
  disableAutoCloseOnConfirm?: boolean
  disableOkButton?: boolean
  children?: React.ReactNode
  secondaryAction?: {
    label: string
    action: () => void
    closeAfterRun?: boolean
    color?:
      | 'inherit'
      | 'primary'
      | 'secondary'
      | 'success'
      | 'error'
      | 'info'
      | 'warning'
  }
  busy?: boolean
}

export const ConfirmationDialog = ({
  title: title,
  message,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  open,
  onClose,
  disableAutoCloseOnConfirm,
  secondaryAction,
  busy,
  ...props
}: ConfirmationDialogProps) => {
  const { t } = useTranslation()

  confirmText = confirmText || t('common:ok')
  cancelText = cancelText || t('common:cancel')
  message =
    message ||
    t('common:irreversible-action', 'This action is not reversible. Continue?')

  const { closeDialog } = useDialogManager()

  const [saving, setSaving] = useState(false)

  const [operationError, setOperationError] = useState<string | null>(null)

  const handleConfirm = useCallback(async () => {
    if (!disableAutoCloseOnConfirm) {
      onClose()
      closeDialog(true)
    }

    if (onConfirm) {
      setSaving(true)
      setTimeout(async () => {
        try {
          await onConfirm()
        } catch (err) {
          setOperationError((err as Error).message)
        } finally {
          setSaving(false)
        }
      })
    }
  }, [closeDialog, disableAutoCloseOnConfirm, onClose, onConfirm])

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel()
    }
    onClose()
  }, [onCancel, onClose])

  useEffect(() => {
    const keydown = async (e: KeyboardEvent) => {
      if (!open) {
        return
      }

      switch (e.key) {
        case 'Enter':
          await handleConfirm()
          break
        case 'Escape':
          handleCancel()
          break
      }
    }

    if (open) {
      window.addEventListener('keydown', keydown)
    }

    return () => {
      window.removeEventListener('keydown', keydown)
    }
  }, [handleCancel, handleConfirm, open])

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullWidth
      {...props}
    >
      <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText
          id="confirmation-dialog-description"
          style={{ whiteSpace: 'pre-line' }}
          component="div"
        >
          {message}
          {props.children}
          {operationError && (
            <Typography color="error">{operationError}</Typography>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          {cancelText}
        </Button>
        {secondaryAction && (
          <Button
            onClick={() => {
              secondaryAction.action?.()
              if (secondaryAction.closeAfterRun) {
                onClose()
                closeDialog(true)
              }
            }}
            color={secondaryAction.color || 'inherit'}
          >
            {secondaryAction.label}
          </Button>
        )}
        <SaveButton
          saving={saving}
          onClick={async () => {
            await handleConfirm()
          }}
          disabled={props.disableOkButton}
          color="primary"
          autoFocus
        >
          {confirmText}
        </SaveButton>
      </DialogActions>
    </Dialog>
  )
}
